import { LngLat, Popup } from "react-map-gl/mapbox";
import { useContext, useMemo, useRef } from "react";
import { FilterType } from "@app/projects/enumerators";
import { GapDensityMapDataContext } from "@app/projects/network-assessment/maps/gap-density-map/contexts/GapDensityMapDataContext";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import ProjectServiceAreaMapSpecialty from "@app/projects/projectServiceAreaMapSpecialty";

type GapsExceptionData = {
  accessGaps: number,
  accessPercent: string | number,
  approvedExceptions: number,
  approvedPercent: string | number,
  gapsPercent: string | number,
  servicingGaps: number,
  servicingPercent: string | number,
  totalExceptions: number,
  totalGaps: number,
  totalPossibleGaps: number,
}

interface GapDensityMapCountyInfoPopupProps {
  isCountySelectionPopup: boolean;
}

const GapDensityMapCountyInfoPopup: React.FC<GapDensityMapCountyInfoPopupProps> = ({isCountySelectionPopup}: GapDensityMapCountyInfoPopupProps) => {

  const projectContext = useContext(CountyProjectContext);
  const mapDataContext = useContext(GapDensityMapDataContext);
  const popup = useRef(null);

  const specialtyFilter = useMemo(() => projectContext.filters.find(f => f.filterType === FilterType.Specialty), [projectContext.filters]);

  const countyInfo = !isCountySelectionPopup ? mapDataContext.hoveredCountyMap : mapDataContext.selectedCountyFeature;

  const showCountyInfo = !mapDataContext.selectedCountyFeature || isCountySelectionPopup;

  const selectedCountyCenter = useMemo<LngLat>(() => {
  if (!mapDataContext.selectedCountyFeature) {
    return null;
  }
    return mapDataContext.getBoundingBoxFeature(mapDataContext.selectedCountyFeature)?.getCenter();
  }, [mapDataContext.selectedCountyFeature]);

  const coordinates = !isCountySelectionPopup ? mapDataContext.mouseCoordinates : selectedCountyCenter;

  const handlePopupClose = () => {
    // This method is meant to remove the county filter from the URL when you close the county popup, but it affects the
    // URL, so we only want to fire the method if the Popup component is mounted
    if (!!popup.current) {
      mapDataContext.setCountyFilter("");
    }
  }

  const toFormattedPercentile = (n: number): string => {
    return n.toFixed(1).padStart(4, '0');
  }

  const gapsExceptionData = useMemo<GapsExceptionData>(() => {
    if (!countyInfo) {
      return null;
    }

    const specialties: ProjectServiceAreaMapSpecialty[] =
      !!specialtyFilter
        ? countyInfo.properties.specialties
          .filter(specialty => specialty.specialtyCode === specialtyFilter.filterValue)
        : countyInfo.properties.specialties;

    if (specialties.length > 0) {
      const totalPossibleGaps = specialties.length;
      const totalGaps = specialties.filter(f => f.isGap)?.length;
      const accessGaps = specialties.filter(f => !f.meetsAccess)?.length;
      const servicingGaps = specialties.filter(f => !f.meetsServicing)?.length;
      const approvedExceptions = specialties.filter(f => f.isExceptionApproved)?.length;
      const totalExceptions = specialties.filter(f => f.hasException)?.length;

      const gapsPercent = toFormattedPercentile((totalGaps / totalPossibleGaps * 1000) / 10);
      const accessPercent = toFormattedPercentile((accessGaps / totalPossibleGaps * 1000) / 10);
      const servicingPercent = toFormattedPercentile((servicingGaps / totalPossibleGaps * 1000) / 10);
      const approvedPercent = toFormattedPercentile(totalExceptions !== 0 ? (approvedExceptions / totalExceptions * 1000) / 10 : 0);
      return {
        accessGaps,
        accessPercent,
        approvedExceptions,
        approvedPercent,
        gapsPercent,
        servicingGaps,
        servicingPercent,
        totalExceptions,
        totalGaps,
        totalPossibleGaps,
      }
    } else {
      return null;
    }
  }, [countyInfo?.properties.specialties, specialtyFilter]);

  const showHoverPopup = (!isCountySelectionPopup && !!countyInfo && countyInfo !== mapDataContext.selectedCountyFeature);
  const showSelectionPopup = (isCountySelectionPopup && !!mapDataContext.selectedCountyFeature);

  return (
    <>
    { (showHoverPopup || showSelectionPopup) &&
      <Popup
        ref={popup}
        closeButton={isCountySelectionPopup}
        onClose={handlePopupClose}
        closeOnClick={false}
        latitude={coordinates.lat}
        longitude={coordinates.lng}
        offset={[0, 0]}
        className="gap-density-map__county-info-popup"
      >
        <div>
          <h5 className="county-info-popup__title">
            {countyInfo.properties.name}, {countyInfo.properties.state}
          </h5>
        </div>
        {showCountyInfo &&
          <>
            <div className="county-info-popup__separator"></div>
            <div className="county-info-popup__row">
              <p>County SSA:</p>
              <p>{countyInfo.properties.countySSA}</p>
            </div>
            <div className="county-info-popup__row">
              <p>County Class:</p>
              <p>{countyInfo.properties.countyClass}</p>
            </div>
            <div className="county-info-popup__row">
              <p>County Status:</p>
              <p>{countyInfo.properties.status}</p>
            </div>
            {(!gapsExceptionData) && (
              <p>Gap density is not available for informational specialty groups.</p>
            )}
            {(!!gapsExceptionData) && (
              <>
                <div className="county-info-popup__row">
                  <p>Total Gaps:</p>
                  <p>{gapsExceptionData.totalGaps} of {gapsExceptionData.totalPossibleGaps} ({gapsExceptionData.gapsPercent}%)</p>
                </div>
                <div className="county-info-popup__row">
                  <p>Servicing Gaps:</p>
                  <p>{gapsExceptionData.servicingGaps} of {gapsExceptionData.totalPossibleGaps} ({gapsExceptionData.servicingPercent}%)</p>
                </div>
                <div className="county-info-popup__row">
                  <p>Access Gaps:</p>
                  <p>{gapsExceptionData.accessGaps} of {gapsExceptionData.totalPossibleGaps} ({gapsExceptionData.accessPercent}%)</p>
                </div>
                {gapsExceptionData.approvedExceptions > 0 &&
                  <div className="county-info-popup__row">
                    <p>Approved Exceptions:</p>
                    <p>{gapsExceptionData.approvedExceptions} of {gapsExceptionData.totalExceptions} ({gapsExceptionData.approvedPercent}%)</p>
                  </div>
                }
              </>
            )}
          </>
        }
      </Popup>
    }
    </>
  );
}

export { GapDensityMapCountyInfoPopup };